/**
 * Profile page (`/profile`).
 *
 * Wave C page 3 of 3 — the user's personal hub. Surfaces identity,
 * reputation, wallet, and settings without ever fetching deals (those
 * live in their own page). The component is intentionally a single file
 * because every section is read-only or near-read-only and the cross
 * dependencies (wallet mismatch + i18n + Zustand) are easier to reason
 * about co-located.
 *
 * Data flow
 * ─────────
 *  1. Mount: hydrate from the Zustand `user` snapshot if present, else
 *     fall back to a fresh `users.getMe()`. The snapshot is lean
 *     (`UserSnapshot`) — for the rich reputation/volume fields we always
 *     need a `UserProfile`, so we keep a separate `profile` state.
 *  2. The wallet card detects mismatches between TonConnect's currently
 *     connected address (`useTonAddress`) and the address persisted on
 *     the backend profile. The user can resolve the mismatch by sending
 *     `users.updateWallet` to push the new address.
 *  3. Disconnect: the backend's Zod validator requires a non-empty
 *     `tonAddress` (see `src/api/validators/user.schema.ts`), so we can
 *     only break the local TonConnect session. The user is informed via
 *     a tooltip that the server-side record will be replaced when they
 *     connect a different wallet.
 *
 * BigInt safety
 * ─────────────
 * `totalVolume` is serialized as a string. We parse it through `BigInt`
 * before passing it to `nanoToTon`. We never call `Number()` on it —
 * the force-bigint hook would block the diff anyway and precision would
 * silently degrade past ~9 TON.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Copy,
  ExternalLink,
  Globe,
  RefreshCw,
  ShieldCheck,
  Wallet,
} from 'lucide-react';

import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { isApiError, users } from '@/lib/api';
import type { UserProfile } from '@/lib/api/types';
import { useI18n, useT, type Locale } from '@/lib/i18n';
import { useAppStore, type UserSnapshot } from '@/lib/state/store';
import { env } from '@/config/env';
import { nanoToTon, shortAddress } from '@/lib/ton/format';
import { haptics } from '@/lib/telegram/haptics';
import { useThemeParams } from '@/lib/telegram/webapp';
import { cn } from '@/lib/utils/cn';

// ─── Constants ──────────────────────────────────────────────

/** Hardcoded so a missing PACKAGE_VERSION env var never blanks the row. */
const APP_VERSION = '0.1.0';

/** Public-facing marketing site. Placeholder until the real domain is live. */
const WEBSITE_URL = 'https://0xcent.app';
const PRIVACY_URL = 'https://0xcent.app/privacy';
const TERMS_URL = 'https://0xcent.app/terms';

// ─── Types ──────────────────────────────────────────────────

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; profile: UserProfile }
  | { kind: 'error'; message: string };

type RoleBadgeVariant = 'default' | 'gold' | 'outline';

const ROLE_BADGE_VARIANT: Record<UserProfile['role'], RoleBadgeVariant> = {
  user: 'outline',
  arbiter: 'gold',
  admin: 'default',
};

// ─── Helpers ────────────────────────────────────────────────

/** Map a server `UserProfile` to the lean `UserSnapshot` shape kept in Zustand. */
function toSnapshot(user: UserProfile): UserSnapshot {
  return {
    id: user.id,
    telegramId: user.telegramId,
    username: user.username,
    tonAddress: user.tonAddress,
    reputationScore: user.reputationScore,
    role: user.role,
    isBanned: user.isBanned,
  };
}

/** Resolve the explorer base URL for the active TON network. */
function explorerBase(): string {
  return env.VITE_TON_NETWORK === 'mainnet'
    ? 'https://tonviewer.com'
    : 'https://testnet.tonviewer.com';
}

function explorerAddressUrl(address: string): string {
  return `${explorerBase()}/${encodeURIComponent(address)}`;
}

/** Pull the first display character for the avatar placeholder. */
function avatarInitial(profile: UserProfile, anonymousLabel: string): string {
  const source =
    profile.firstName ?? profile.username ?? anonymousLabel ?? '?';
  const trimmed = source.trim();
  if (trimmed.length === 0) return '?';
  // Use codePointAt + String.fromCodePoint to handle emoji + non-Latin
  // alphabets gracefully (e.g. Turkish "İ").
  const first = trimmed.codePointAt(0);
  if (first === undefined) return '?';
  return String.fromCodePoint(first).toUpperCase();
}

/** Build the full display name with sensible fallbacks. */
function displayName(profile: UserProfile, anonymousLabel: string): string {
  if (profile.firstName) {
    return profile.lastName
      ? `${profile.firstName} ${profile.lastName}`
      : profile.firstName;
  }
  if (profile.username) return `@${profile.username}`;
  return anonymousLabel;
}

/** Parse a string-encoded bigint defensively. */
function safeBigInt(value: string): bigint | null {
  try {
    return BigInt(value);
  } catch {
    return null;
  }
}

// ─── Skeleton screens ───────────────────────────────────────

function ProfileSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <Card className="flex items-center gap-4">
        <Skeleton className="h-16 w-16 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-1/3" />
        </div>
      </Card>
      <Card className="flex flex-col gap-3">
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-3 w-full" />
        <div className="flex gap-2">
          <Skeleton className="h-16 flex-1" />
          <Skeleton className="h-16 flex-1" />
          <Skeleton className="h-16 flex-1" />
        </div>
      </Card>
      <Card className="flex flex-col gap-3">
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-10 w-full" />
      </Card>
      <Card className="flex flex-col gap-3">
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </Card>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────

interface SectionTitleProps {
  children: React.ReactNode;
}

function SectionTitle({ children }: SectionTitleProps): JSX.Element {
  return (
    <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">
      {children}
    </h2>
  );
}

interface CopyButtonProps {
  value: string;
  ariaLabel: string;
}

function CopyButton({ value, ariaLabel }: CopyButtonProps): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleClick = useCallback(() => {
    haptics.light();
    void navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Clipboard can fail in non-secure contexts; degrade silently.
      });
  }, [value]);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center justify-center rounded-md p-1.5 text-text-muted',
        'hover:text-text-primary hover:bg-bg-secondary/60 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
      )}
    >
      {copied ? (
        <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden />
      )}
    </button>
  );
}

interface StatTileProps {
  value: string | number;
  label: string;
  tone?: 'default' | 'success' | 'danger';
}

function StatTile({ value, label, tone = 'default' }: StatTileProps): JSX.Element {
  const valueClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'danger'
        ? 'text-danger'
        : 'text-text-primary';

  return (
    <div className="flex flex-1 flex-col items-center gap-1 rounded-md border border-border/40 bg-bg-secondary/40 px-2 py-3 text-center">
      <span className={cn('text-xl font-bold leading-none', valueClass)}>
        {value}
      </span>
      <span className="text-[10px] uppercase tracking-wide text-text-muted">
        {label}
      </span>
    </div>
  );
}

interface SettingRowProps {
  label: string;
  children: React.ReactNode;
}

function SettingRow({ label, children }: SettingRowProps): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/30 py-3 last:border-0">
      <span className="text-sm text-text-secondary">{label}</span>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

interface ExternalRowProps {
  href: string;
  label: string;
}

function ExternalRow({ href, label }: ExternalRowProps): JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex items-center justify-between gap-3 border-b border-border/30 py-3 last:border-0',
        'text-sm text-text-secondary transition-colors hover:text-text-primary',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
      )}
      onClick={() => haptics.light()}
    >
      <span>{label}</span>
      <ExternalLink className="h-3.5 w-3.5 text-text-muted" aria-hidden />
    </a>
  );
}

// ─── Page ───────────────────────────────────────────────────

export function ProfilePage(): JSX.Element {
  const t = useT();
  const { locale, setLocale } = useI18n();
  const setUser = useAppStore((state) => state.setUser);
  const userSnapshot = useAppStore((state) => state.user);

  const tonAddress = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  const { scheme } = useThemeParams();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [walletUpdating, setWalletUpdating] = useState(false);
  const [walletFlash, setWalletFlash] = useState<
    { kind: 'success' | 'error'; message: string } | null
  >(null);

  // ── Loader ──────────────────────────────────────────────
  const load = useCallback(async (): Promise<void> => {
    setState({ kind: 'loading' });
    try {
      const { user } = await users.getMe();
      setUser(toSnapshot(user));
      setState({ kind: 'ready', profile: user });
    } catch (err) {
      const message =
        isApiError(err) || err instanceof Error
          ? err.message
          : t('profile.error.loadFailed');
      setState({ kind: 'error', message });
    }
  }, [setUser, t]);

  // Initial fetch — even if a snapshot exists, we need the rich
  // `UserProfile` (totalVolume, totalDeals, ...) which the snapshot
  // does not carry. The snapshot still gives us a non-blank header
  // while the request is in flight.
  useEffect(() => {
    void load();
  }, [load]);

  // ── Wallet handlers ─────────────────────────────────────
  const persistWallet = useCallback(
    async (nextAddress: string | null): Promise<void> => {
      if (state.kind !== 'ready') return;
      setWalletUpdating(true);
      setWalletFlash(null);
      try {
        const { user } = await users.updateWallet({ tonAddress: nextAddress });
        setUser(toSnapshot(user));
        setState({ kind: 'ready', profile: user });
        haptics.success();
        setWalletFlash({
          kind: 'success',
          message: nextAddress
            ? t('profile.wallet.updateSuccess')
            : t('profile.wallet.disconnectSuccess'),
        });
      } catch (err) {
        haptics.error();
        const message =
          isApiError(err) || err instanceof Error
            ? err.message
            : t('profile.wallet.updateFailed');
        setWalletFlash({ kind: 'error', message });
      } finally {
        setWalletUpdating(false);
      }
    },
    [setUser, state, t],
  );

  // Open Tonkeeper directly (skips the multi-wallet picker modal).
  const handleConnect = useCallback((): void => {
    haptics.medium();
    void tonConnectUI.openSingleWalletModal('tonkeeper');
  }, [tonConnectUI]);

  // Full disconnect: clear server-side then disconnect TonConnect client.
  // Backend now accepts tonAddress: null as a valid clear operation.
  const handleDisconnect = useCallback(async (): Promise<void> => {
    haptics.medium();
    try {
      await persistWallet(null);
    } catch {
      // persistWallet already surfaces its own error state
    }
    try {
      await tonConnectUI.disconnect();
    } catch {
      // Disconnect failures usually mean the wallet is already gone.
    }
  }, [persistWallet, tonConnectUI]);

  const handleUpdateMismatch = useCallback((): void => {
    if (!tonAddress) return;
    void persistWallet(tonAddress);
  }, [persistWallet, tonAddress]);

  const handleRetry = useCallback((): void => {
    haptics.light();
    void load();
  }, [load]);

  const handleLocaleChange = useCallback(
    (next: Locale): void => {
      if (next === locale) return;
      haptics.selectionChanged();
      setLocale(next);
    },
    [locale, setLocale],
  );

  // ── Derived view-model ──────────────────────────────────
  const view = useMemo(() => {
    if (state.kind !== 'ready') return null;
    const { profile } = state;

    const totalVolumeNano = safeBigInt(profile.totalVolume);
    const totalVolumeDisplay =
      totalVolumeNano !== null ? `${nanoToTon(totalVolumeNano, 2)} TON` : '— TON';

    const reputationScore = Math.max(0, Math.min(100, profile.reputationScore));
    const reputationPct = `${reputationScore}%`;

    const profileAddress = profile.tonAddress;
    const connectedAddress = tonAddress || null;
    const mismatch =
      profileAddress !== null &&
      connectedAddress !== null &&
      profileAddress !== connectedAddress;

    const anonymousLabel = t('profile.identity.anonymous');

    return {
      profile,
      totalVolumeDisplay,
      reputationScore,
      reputationPct,
      profileAddress,
      connectedAddress,
      mismatch,
      anonymousLabel,
      avatar: avatarInitial(profile, anonymousLabel),
      name: displayName(profile, anonymousLabel),
    };
  }, [state, t, tonAddress]);

  // ── Render branches ─────────────────────────────────────

  if (state.kind === 'loading' && !userSnapshot) {
    return (
      <AppShell>
        <PageHeader title={t('profile.title')} />
        <ProfileSkeleton />
        <span className="sr-only">{t('common.loading')}</span>
      </AppShell>
    );
  }

  if (state.kind === 'error') {
    return (
      <AppShell>
        <PageHeader title={t('profile.title')} />
        <Card className="flex flex-col items-center gap-3 py-8 text-center">
          <div className="rounded-full bg-danger/15 p-3 text-danger">
            <AlertCircle className="h-6 w-6" aria-hidden />
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-text-primary">
              {t('profile.error.loadFailed')}
            </h3>
            <p className="max-w-sm text-xs text-text-muted">{state.message}</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleRetry}>
            <RefreshCw className="h-4 w-4" aria-hidden />
            {t('profile.error.retry')}
          </Button>
        </Card>
      </AppShell>
    );
  }

  if (!view) {
    // Should be unreachable: state.kind === 'loading' is handled above and
    // state.kind === 'error' is handled above. Defensive fallback.
    return (
      <AppShell>
        <PageHeader title={t('profile.title')} />
        <ProfileSkeleton />
      </AppShell>
    );
  }

  const { profile } = view;

  return (
    <AppShell>
      <PageHeader title={t('profile.title')} />

      {/* ─── Identity card ─────────────────────────────── */}
      <Card className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <div
            aria-hidden
            className={cn(
              'flex h-16 w-16 shrink-0 items-center justify-center rounded-full',
              'bg-gradient-to-br from-accent-purple/30 to-accent-gold/20',
              'text-2xl font-bold text-text-primary',
            )}
          >
            {view.avatar}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-semibold text-text-primary">
              {view.name}
            </p>
            <p className="mt-0.5 text-xs text-text-muted">
              {t('profile.identity.telegramId')}: {profile.telegramId}
            </p>
            <div className="mt-2">
              <Badge variant={ROLE_BADGE_VARIANT[profile.role]}>
                {t(`profile.identity.role.${profile.role}` as const)}
              </Badge>
            </div>
          </div>
        </div>

        {profile.isBanned && (
          <div className="flex items-start gap-3 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-text-primary">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden />
            <div className="space-y-1">
              <p className="font-semibold text-danger">
                {t('profile.identity.banned.title')}
              </p>
              <p className="text-xs text-text-secondary">
                {t('profile.identity.banned.body', {
                  reason: profile.banReason ?? '—',
                })}
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* ─── Reputation card ───────────────────────────── */}
      <Card className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <SectionTitle>{t('profile.reputation.title')}</SectionTitle>
          <ShieldCheck className="h-4 w-4 text-accent-gold" aria-hidden />
        </div>

        <div className="flex items-end gap-2">
          <span className="bg-gradient-to-r from-accent-gold to-accent-purple bg-clip-text text-5xl font-bold leading-none text-transparent">
            {view.reputationScore}
          </span>
          <span className="pb-1 text-sm text-text-muted">
            {t('profile.reputation.scoreLabel', { score: view.reputationScore })}
          </span>
        </div>

        <div
          className="h-2 overflow-hidden rounded-full bg-bg-secondary/60"
          role="progressbar"
          aria-valuenow={view.reputationScore}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('profile.reputation.title')}
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-accent-gold via-accent-gold to-accent-purple transition-all"
            style={{ width: view.reputationPct }}
          />
        </div>

        <div className="flex items-stretch gap-2">
          <StatTile
            value={profile.totalDeals}
            label={t('profile.reputation.totalDeals')}
          />
          <StatTile
            value={profile.completedDeals}
            label={t('profile.reputation.completed')}
            tone="success"
          />
          <StatTile
            value={profile.disputedDeals}
            label={t('profile.reputation.disputed')}
            tone={profile.disputedDeals > 0 ? 'danger' : 'default'}
          />
        </div>

        <div className="flex items-center justify-between rounded-md border border-border/40 bg-bg-secondary/40 px-3 py-2">
          <span className="text-xs uppercase tracking-wide text-text-muted">
            {t('profile.reputation.totalVolume')}
          </span>
          <span className="text-sm font-semibold text-accent-gold">
            {view.totalVolumeDisplay}
          </span>
        </div>

        <p className="text-[11px] leading-relaxed text-text-muted">
          {t('profile.reputation.footer')}
        </p>
      </Card>

      {/* ─── Wallet card ───────────────────────────────── */}
      <Card className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <SectionTitle>{t('profile.wallet.title')}</SectionTitle>
          <Wallet className="h-4 w-4 text-text-muted" aria-hidden />
        </div>

        {view.profileAddress ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2 rounded-md border border-border/40 bg-bg-secondary/40 px-3 py-2">
              <span
                className="font-mono text-xs text-text-primary"
                title={view.profileAddress}
              >
                {shortAddress(view.profileAddress, 6, 6)}
              </span>
              <CopyButton
                value={view.profileAddress}
                ariaLabel={t('common.copy')}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <a
                href={explorerAddressUrl(view.profileAddress)}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'inline-flex items-center gap-1.5 text-xs font-medium text-accent-purple',
                  'hover:underline focus-visible:outline-none focus-visible:ring-2',
                  'focus-visible:ring-accent-purple focus-visible:ring-offset-2',
                  'focus-visible:ring-offset-bg-primary',
                )}
                onClick={() => haptics.light()}
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                {t('profile.wallet.viewOnExplorer')}
              </a>
              <Button
                variant="gold"
                size="sm"
                onClick={handleConnect}
                disabled={walletUpdating}
              >
                <Wallet className="h-4 w-4" aria-hidden />
                {t('profile.wallet.reconnect')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDisconnect}
                disabled={walletUpdating}
              >
                {t('profile.wallet.disconnect')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-text-secondary">
              {t('profile.wallet.empty')}
            </p>
            <Button variant="gold" size="sm" onClick={handleConnect}>
              <Wallet className="h-4 w-4" aria-hidden />
              {t('profile.wallet.connect')}
            </Button>
          </div>
        )}

        {view.mismatch && (
          <div className="flex flex-col gap-2 rounded-md border border-warning/40 bg-warning/10 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle
                className="mt-0.5 h-4 w-4 shrink-0 text-warning"
                aria-hidden
              />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-warning">
                  {t('profile.wallet.mismatch.title')}
                </p>
                <p className="text-xs text-text-secondary">
                  {t('profile.wallet.mismatch.body')}
                </p>
                {view.connectedAddress && (
                  <p className="font-mono text-[11px] text-text-muted">
                    {shortAddress(view.connectedAddress, 6, 6)}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleUpdateMismatch}
              disabled={walletUpdating}
            >
              {walletUpdating
                ? t('common.loading')
                : t('profile.wallet.mismatch.cta')}
            </Button>
          </div>
        )}

        {walletFlash && (
          <p
            className={cn(
              'text-xs',
              walletFlash.kind === 'success' ? 'text-success' : 'text-danger',
            )}
          >
            {walletFlash.message}
          </p>
        )}
      </Card>

      {/* ─── Settings card ─────────────────────────────── */}
      <Card className="flex flex-col gap-1">
        <div className="flex items-center justify-between pb-2">
          <SectionTitle>{t('profile.settings.title')}</SectionTitle>
          <Globe className="h-4 w-4 text-text-muted" aria-hidden />
        </div>

        <SettingRow label={t('profile.settings.language')}>
          <div
            className="inline-flex items-center rounded-md border border-border/60 bg-bg-secondary/40 p-0.5"
            role="tablist"
            aria-label={t('profile.settings.language')}
          >
            <button
              type="button"
              role="tab"
              aria-selected={locale === 'tr'}
              onClick={() => handleLocaleChange('tr')}
              className={cn(
                'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                locale === 'tr'
                  ? 'bg-accent-purple text-white'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              TR
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={locale === 'en'}
              onClick={() => handleLocaleChange('en')}
              className={cn(
                'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                locale === 'en'
                  ? 'bg-accent-purple text-white'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              EN
            </button>
          </div>
        </SettingRow>

        <SettingRow label={t('profile.settings.theme')}>
          <span className="text-xs text-text-muted">
            {scheme === 'light' ? '☀' : '🌙'} {t('profile.settings.themeControlled')}
          </span>
        </SettingRow>
      </Card>

      {/* ─── About card ────────────────────────────────── */}
      <Card className="flex flex-col gap-1">
        <div className="flex items-center justify-between pb-2">
          <SectionTitle>{t('profile.settings.about')}</SectionTitle>
        </div>

        <SettingRow label={t('profile.settings.version', { version: APP_VERSION })}>
          <span className="font-mono text-xs text-text-muted">v{APP_VERSION}</span>
        </SettingRow>
        <ExternalRow href={WEBSITE_URL} label={t('profile.settings.website')} />
        <ExternalRow href={PRIVACY_URL} label={t('profile.settings.privacy')} />
        <ExternalRow href={TERMS_URL} label={t('profile.settings.terms')} />
      </Card>
    </AppShell>
  );
}

// Re-export for the router import. Keeping this explicit avoids drift if
// the file ever ships a default export.
export default ProfilePage;
